import { FormSelectField, SelectProps } from 'omakase';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import React from 'react';
import phoneCountryCodes, {
  SANCTIONED_PHONE_COUNTRY_CODES_SET,
} from '@tock/shared/constants/phoneCountryCodes';

type FormPhoneCountryCodeFieldOwnProps = {
  phoneFieldName?: string;
  disallowSanctionedCodes?: boolean;
  children?: React.ReactNode;
};

type FormPhoneCountryCodeFieldProps = FormPhoneCountryCodeFieldOwnProps &
  Omit<SelectProps, 'children'>;

/**
 * Formik-ready select field that includes all ISO phone country codes as options
 */
const FormPhoneCountryCodeField: React.FunctionComponent<FormPhoneCountryCodeFieldProps> = ({
  name = 'phoneCountryCode',
  id = 'phoneCountryCode',
  phoneFieldName = 'phone',
  label = 'Phone country code',
  placeholder = 'Select a country code',
  disallowSanctionedCodes = false,
  onChange,
  ...rest
}) => {
  const canDisallowSanctionedCodes = useFeatureFlagForConsumer(
    'DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION'
  );
  disallowSanctionedCodes = disallowSanctionedCodes && canDisallowSanctionedCodes;

  return (
    <FormSelectField
      name={name}
      id={id}
      label={label}
      onChange={onChange}
      placeholder={placeholder}
      inputProps={{
        'data-testid': 'phone-country-code',
        autoComplete: 'tel-country-code',
      }}
      {...rest}
    >
      {phoneCountryCodes.map(({ phone, iso, name }) => (
        <option
          key={`${phone} ${iso}`}
          value={`${phone} ${iso}`}
          disabled={disallowSanctionedCodes && SANCTIONED_PHONE_COUNTRY_CODES_SET.has(phone)}
        >
          {name}&nbsp;{phone}
        </option>
      ))}
    </FormSelectField>
  );
};

export default FormPhoneCountryCodeField;
